import React, { useState } from 'react';
import { Shield, Plus, Pencil, Trash2, Loader2, X, Check, AlertCircle, Info, UserCheck } from 'lucide-react';
import axios from 'axios';
import { extractErrorMessage } from '@/utils/errorHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ID_DOC_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'state_id', label: 'State ID' },
  { value: 'foreign_passport', label: 'Foreign Passport' },
];

const CONTROL_BASIS_OPTIONS = [
  { value: 'senior_officer', label: 'Senior Officer' },
  { value: 'board_authority', label: 'Board Authority' },
  { value: 'important_decision_maker', label: 'Important Decision Maker' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  full_legal_name: '', email: '', date_of_birth: '',
  residential_address: { street: '', city: '', state: '', postal: '', country: 'US' },
  id_document_type: '', id_document_number_last4: '', id_document_country: 'US',
  ownership_percentage: '', has_substantial_control: false, control_basis: '',
};

const inputCls = "w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]";
const labelCls = "block text-xs text-gray-400 mb-1";

// ── BO Form (used for both Add and Edit) ──
const BOForm = ({ form, setForm, onSubmit, onCancel, loading, error, isEdit }) => {
  const handleField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const handleAddr = (field, value) => setForm(prev => ({
    ...prev, residential_address: { ...prev.residential_address, [field]: value }
  }));

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Full Legal Name *</label>
          <input type="text" value={form.full_legal_name} onChange={e => handleField('full_legal_name', e.target.value)}
            className={inputCls} placeholder="Jane Doe" data-testid="bo-name-input" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" value={form.email} onChange={e => handleField('email', e.target.value)}
            className={inputCls} placeholder="jane@example.com" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Date of Birth *</label>
          <input type="date" value={form.date_of_birth} onChange={e => handleField('date_of_birth', e.target.value)}
            className={inputCls} data-testid="bo-dob-input" />
        </div>
        <div>
          <label className={labelCls}>Ownership % (0-100)</label>
          <input type="number" min="0" max="100" step="0.01" value={form.ownership_percentage}
            onChange={e => handleField('ownership_percentage', e.target.value)}
            className={inputCls} placeholder="25" data-testid="bo-pct-input" />
        </div>
      </div>

      {/* Substantial Control */}
      <div className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.has_substantial_control}
            onChange={e => handleField('has_substantial_control', e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 text-[#059669] focus:ring-[#059669] bg-gray-700"
            data-testid="bo-control-check" />
          <span className="text-sm text-gray-300">This person exercises substantial control</span>
        </label>
        {form.has_substantial_control && (
          <div>
            <label className={labelCls}>Control Basis *</label>
            <select value={form.control_basis} onChange={e => handleField('control_basis', e.target.value)}
              className={inputCls} data-testid="bo-control-basis-select">
              <option value="">Select basis...</option>
              {CONTROL_BASIS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Address */}
      <div>
        <label className={labelCls}>Residential Address *</label>
        <div className="space-y-2">
          <input type="text" value={form.residential_address.street} onChange={e => handleAddr('street', e.target.value)}
            className={inputCls} placeholder="Street address" data-testid="bo-street-input" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input type="text" value={form.residential_address.city} onChange={e => handleAddr('city', e.target.value)}
              className={inputCls} placeholder="City" />
            <input type="text" value={form.residential_address.state} onChange={e => handleAddr('state', e.target.value)}
              className={inputCls} placeholder="State" />
            <input type="text" value={form.residential_address.postal} onChange={e => handleAddr('postal', e.target.value)}
              className={inputCls} placeholder="ZIP" />
            <input type="text" value={form.residential_address.country} onChange={e => handleAddr('country', e.target.value)}
              className={inputCls} placeholder="US" />
          </div>
        </div>
      </div>

      {/* ID Document */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>ID Document Type *</label>
          <select value={form.id_document_type} onChange={e => handleField('id_document_type', e.target.value)}
            className={inputCls} data-testid="bo-doc-type-select">
            <option value="">Select...</option>
            {ID_DOC_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Last 4 of Doc # *</label>
          <input type="text" maxLength={4} value={form.id_document_number_last4}
            onChange={e => handleField('id_document_number_last4', e.target.value)}
            className={inputCls} placeholder="1234" data-testid="bo-doc-last4-input" />
        </div>
        <div>
          <label className={labelCls}>Issuing Country *</label>
          <input type="text" maxLength={2} value={form.id_document_country}
            onChange={e => handleField('id_document_country', e.target.value.toUpperCase())}
            className={inputCls} placeholder="US" />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={loading}
          className="flex-1 py-2.5 bg-[#059669] text-black font-semibold rounded-lg hover:bg-[#00D46E] disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          data-testid="bo-form-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {isEdit ? 'Update' : 'Add Beneficial Owner'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2.5 bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
};


// ── Main Section ──
const BeneficialOwnersSection = ({ companyId, company, token, onDataChange, autoOpenAdd }) => {
  const [bos, setBos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBOs = async () => {
    try {
      const r = await axios.get(`${API}/companies/${companyId}/beneficial-owners`, { headers });
      setBos(r.data.beneficial_owners || []);
    } catch {
      setBos([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchBOs(); }, [companyId, token]);

  // Auto-open add form when triggered by parent (e.g., setup banner)
  React.useEffect(() => {
    if (autoOpenAdd && !loading && bos.length === 0) {
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      setShowForm(true);
    }
  }, [autoOpenAdd, loading]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setShowForm(false);
    setEditingId(null);
    setFormError('');
  };

  const validateForm = () => {
    if (!form.full_legal_name.trim()) return 'Full legal name is required';
    if (!form.date_of_birth) return 'Date of birth is required';
    if (!form.residential_address.street || !form.residential_address.city || !form.residential_address.state || !form.residential_address.postal)
      return 'Complete residential address is required';
    if (!form.id_document_type) return 'ID document type is required';
    if (!form.id_document_number_last4) return 'Last 4 of document number is required';
    if (!form.id_document_country) return 'Issuing country is required';
    const pct = form.ownership_percentage ? parseFloat(form.ownership_percentage) : null;
    if (!form.has_substantial_control && (!pct || pct <= 0))
      return 'BO must either own >0% or exercise substantial control (at least one CTA test)';
    if (form.has_substantial_control && !form.control_basis)
      return 'Control basis is required when substantial control is checked';
    return null;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setFormLoading(true); setFormError('');
    try {
      const payload = {
        ...form,
        full_legal_name: form.full_legal_name.trim(),
        ownership_percentage: form.ownership_percentage ? parseFloat(form.ownership_percentage) : null,
        control_basis: form.has_substantial_control ? form.control_basis : null,
      };
      await axios.post(`${API}/companies/${companyId}/beneficial-owners`, payload, { headers });
      resetForm();
      fetchBOs();
      if (onDataChange) onDataChange();
    } catch (err) {
      setFormError(extractErrorMessage(err, 'Failed to add beneficial owner'));
    } finally {
      setFormLoading(false);
    }
  };

  const startEdit = (bo) => {
    setEditingId(bo.id);
    setShowForm(false);
    setForm({
      full_legal_name: bo.full_legal_name || '',
      email: bo.email || '',
      date_of_birth: bo.date_of_birth || '',
      residential_address: bo.residential_address || { street: '', city: '', state: '', postal: '', country: 'US' },
      id_document_type: bo.id_document_type || '',
      id_document_number_last4: bo.id_document_number_last4 || '',
      id_document_country: bo.id_document_country || 'US',
      ownership_percentage: bo.ownership_percentage ?? '',
      has_substantial_control: bo.has_substantial_control || false,
      control_basis: bo.control_basis || '',
    });
    setFormError('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setFormLoading(true); setFormError('');
    try {
      const payload = {
        ...form,
        full_legal_name: form.full_legal_name.trim(),
        ownership_percentage: form.ownership_percentage ? parseFloat(form.ownership_percentage) : null,
        control_basis: form.has_substantial_control ? form.control_basis : null,
      };
      await axios.patch(`${API}/beneficial-owners/${editingId}`, payload, { headers });
      resetForm();
      fetchBOs();
    } catch (err) {
      setFormError(extractErrorMessage(err, 'Failed to update beneficial owner'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (bo) => {
    if (!window.confirm(`Remove ${bo.full_legal_name} as a beneficial owner?`)) return;
    setDeletingId(bo.id);
    try {
      await axios.delete(`${API}/beneficial-owners/${bo.id}`, { headers });
      fetchBOs();
      if (onDataChange) onDataChange();
    } catch (err) {
      alert(extractErrorMessage(err, 'Failed to delete'));
    } finally {
      setDeletingId(null);
    }
  };

  const isDomesticExempt = company && !company.boir_filing_required &&
    ['us_llc', 'us_c_corp', 'us_s_corp', 'us_sole_proprietorship'].includes(company.entity_type);

  return (
    <section className="bg-[#111827] border border-gray-800 rounded-lg p-4 sm:p-6" data-testid="beneficial-owners-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#059669]" />
          Beneficial Owners
        </h2>
        {!showForm && !editingId && (
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="text-sm text-[#059669] px-3 py-1 rounded border border-[#059669] hover:bg-[#059669]/10 flex items-center gap-1 transition-colors"
            data-testid="add-bo-btn">
            <Plus className="w-4 h-4" /> Add Beneficial Owner
          </button>
        )}
      </div>

      {/* Filing-exempt banner */}
      {isDomesticExempt && (
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-start gap-2" data-testid="filing-exempt-banner">
          <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-blue-300 text-xs leading-relaxed">
            Your US-formed entity is currently exempt from BOIR filing under FinCEN's March 2025 interim final rule. We still recommend capturing beneficial ownership information for your records, banking KYC, and in case regulations change.
          </p>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="mb-4 p-4 bg-[#0b1310] border border-gray-700 rounded-lg">
          <h3 className="text-sm font-medium text-white mb-3">Add Beneficial Owner</h3>
          <BOForm form={form} setForm={setForm} onSubmit={handleCreate} onCancel={resetForm}
            loading={formLoading} error={formError} isEdit={false} />
        </div>
      )}

      {/* Edit form */}
      {editingId && (
        <div className="mb-4 p-4 bg-[#0b1310] border border-[#059669]/30 rounded-lg">
          <h3 className="text-sm font-medium text-white mb-3">Edit Beneficial Owner</h3>
          <BOForm form={form} setForm={setForm} onSubmit={handleUpdate} onCancel={resetForm}
            loading={formLoading} error={formError} isEdit={true} />
        </div>
      )}

      {/* BO list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#059669] animate-spin" /></div>
      ) : bos.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="bo-table">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-xs font-medium text-gray-500 pb-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 pb-3 hidden sm:table-cell">Ownership %</th>
                <th className="text-left text-xs font-medium text-gray-500 pb-3 hidden sm:table-cell">Control</th>
                <th className="text-left text-xs font-medium text-gray-500 pb-3 hidden md:table-cell">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bos.map(bo => (
                <tr key={bo.id} className="border-b border-gray-800/50" data-testid={`bo-row-${bo.id}`}>
                  <td className="py-3">
                    <div>
                      <span className="text-white text-sm">{bo.full_legal_name}</span>
                      {bo.member_id && (
                        <span className="ml-2 px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] font-medium rounded" data-testid={`bo-member-badge-${bo.id}`}>
                          Also a Member
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs sm:hidden mt-0.5">
                      {bo.ownership_percentage ? `${bo.ownership_percentage}%` : '—'} | {bo.has_substantial_control ? (bo.control_basis || 'Control').replace(/_/g, ' ') : '—'}
                    </p>
                  </td>
                  <td className="py-3 text-gray-400 text-sm hidden sm:table-cell">
                    {bo.ownership_percentage ? `${bo.ownership_percentage}%` : '—'}
                  </td>
                  <td className="py-3 text-gray-400 text-sm hidden sm:table-cell">
                    {bo.has_substantial_control ? (bo.control_basis || 'Yes').replace(/_/g, ' ') : '—'}
                  </td>
                  <td className="py-3 hidden md:table-cell">
                    {bo.date_of_birth ? (
                      <span className="text-xs text-gray-500">DOB on file</span>
                    ) : (
                      <span className="text-xs text-amber-400">Incomplete</span>
                    )}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => startEdit(bo)} disabled={editingId === bo.id}
                        className="p-1.5 text-gray-500 hover:text-[#059669] transition-colors" data-testid={`bo-edit-${bo.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(bo)} disabled={deletingId === bo.id}
                        className="p-1.5 text-gray-500 hover:text-red-400 transition-colors" data-testid={`bo-delete-${bo.id}`}>
                        {deletingId === bo.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 text-sm text-center py-4" data-testid="bo-empty-state">
          No beneficial owners added yet. Add one to get started.
        </p>
      )}
    </section>
  );
};

export { BeneficialOwnersSection, EMPTY_FORM as BO_EMPTY_FORM, ID_DOC_TYPES, CONTROL_BASIS_OPTIONS };
export default BeneficialOwnersSection;
